import React, { useState, useEffect } from 'react';
import { Navigation } from '../components/Navigation';
import { Newsletter } from '../components/Newsletter';
import { motion } from 'framer-motion';
import { Calculator, DollarSign, Clock, Building2, Heart, Briefcase, Users } from 'lucide-react';
import { FinancialChart } from '../components/Chart';
import { RETIREMENT_ACCOUNTS, STATE_TAX_RATES } from '../constants';

interface SpouseIncome {
  incomeType: 'hourly' | 'salary';
  payRate: number;
  salary: number;
  hoursPerWeek: number;
  selfEmployment: number;
  otherIncome: number;
  federalWithholding: number;
  estimatedTaxPayments: number;
}

interface RetirementContribution {
  type: keyof typeof RETIREMENT_ACCOUNTS;
  amount: number;
}

interface PayPeriod {
  gross: number;
  federal: number;
  state: number;
  local: number;
  socialSecurity: number;
  medicare: number;
  disability: number;
  retirement401k: number;
  insurance: number;
  net: number;
}

const TakeHomeCalculator: React.FC = () => {
  const [incomeType, setIncomeType] = useState<'hourly' | 'salary'>('hourly');
  const [payRate, setPayRate] = useState<number>(20);
  const [salary, setSalary] = useState<number>(50000);
  const [hoursPerWeek, setHoursPerWeek] = useState<number>(40);
  const [payFrequency, setPayFrequency] = useState<'weekly' | 'biweekly' | 'semimonthly' | 'monthly'>('biweekly');
  const [state, setState] = useState<string>('California');
  const [filingStatus, setFilingStatus] = useState<'single' | 'married' | 'head'>('single');
  const [allowances, setAllowances] = useState<number>(0);
  const [age, setAge] = useState<number>(30);
  const [retirementContributions, setRetirementContributions] = useState<RetirementContribution[]>([]);
  const [healthInsurance, setHealthInsurance] = useState<number>(0);
  const [otherDeductions, setOtherDeductions] = useState<number>(0);
  const [showSpouseIncome, setShowSpouseIncome] = useState<boolean>(false);
  
  const [spouseIncome, setSpouseIncome] = useState<SpouseIncome>({
    incomeType: 'hourly',
    payRate: 20,
    salary: 50000,
    hoursPerWeek: 40,
    selfEmployment: 0,
    otherIncome: 0,
    federalWithholding: 0,
    estimatedTaxPayments: 0
  });

  const [payPeriodDetails, setPayPeriodDetails] = useState<PayPeriod>({
    gross: 0,
    federal: 0,
    state: 0,
    local: 0,
    socialSecurity: 0,
    medicare: 0,
    disability: 0,
    retirement401k: 0,
    insurance: 0,
    net: 0
  });

  const addRetirementContribution = () => {
    setRetirementContributions([
      ...retirementContributions,
      { type: 'traditional401k', amount: 0 }
    ]);
  };

  const updateRetirementContribution = (index: number, updates: Partial<RetirementContribution>) => {
    const newContributions = [...retirementContributions];
    newContributions[index] = { ...newContributions[index], ...updates };
    setRetirementContributions(newContributions);
  };

  const removeRetirementContribution = (index: number) => {
    setRetirementContributions(retirementContributions.filter((_, i) => i !== index));
  };

  const calculateStateTax = (annualTaxableIncome: number, state: string): number => {
    const stateRate = STATE_TAX_RATES.find(s => s.state === state)?.rate || 0;
    return annualTaxableIncome * (stateRate / 100);
  };

  const calculatePaycheck = () => {
    const periodsPerYear = {
      weekly: 52,
      biweekly: 26,
      semimonthly: 24,
      monthly: 12
    }[payFrequency];

    const primaryAnnualGross = incomeType === 'hourly' 
      ? payRate * hoursPerWeek * 52 
      : salary;

    const spouseAnnualGross = showSpouseIncome
      ? (spouseIncome.incomeType === 'hourly'
          ? spouseIncome.payRate * spouseIncome.hoursPerWeek * 52
          : spouseIncome.salary) +
        spouseIncome.selfEmployment +
        spouseIncome.otherIncome
      : 0;

    const annualGross = primaryAnnualGross + spouseAnnualGross;
    const grossPerPeriod = annualGross / periodsPerYear;

    // Calculate retirement deductions
    const retirementDeductions = retirementContributions.reduce((total, contribution) => {
      const account = RETIREMENT_ACCOUNTS[contribution.type];
      const annualLimit = age >= (account.catchupAge || 0) 
        ? account.limit + (account.catchup || 0)
        : account.limit;
      
      const periodicLimit = annualLimit / periodsPerYear;
      const periodicContribution = (contribution.amount / 100) * grossPerPeriod;
      
      return total + Math.min(periodicContribution, periodicLimit);
    }, 0);

    // Calculate taxes
    const federalTaxableIncome = annualGross - (retirementDeductions * periodsPerYear);
    const federalTax = calculateFederalTax(federalTaxableIncome) / periodsPerYear;
    const stateTax = calculateStateTax(federalTaxableIncome, state) / periodsPerYear;
    
    const socialSecurityTax = Math.min(grossPerPeriod * 0.062, (160200 / periodsPerYear) * 0.062);
    const medicareTax = grossPerPeriod * 0.0145;
    
    const totalDeductions = federalTax + stateTax + socialSecurityTax + medicareTax + 
                           retirementDeductions + healthInsurance + otherDeductions;
    
    const netPay = grossPerPeriod - totalDeductions;

    setPayPeriodDetails({
      gross: grossPerPeriod,
      federal: federalTax,
      state: stateTax,
      local: 0,
      socialSecurity: socialSecurityTax,
      medicare: medicareTax,
      disability: 0,
      retirement401k: retirementDeductions,
      insurance: healthInsurance,
      net: netPay
    });
  };

  const calculateFederalTax = (annualTaxableIncome: number): number => {
    // 2025 Tax Brackets
    const brackets = {
      single: [
        { rate: 0.10, threshold: 13850 },
        { rate: 0.12, threshold: 24850 },
        { rate: 0.22, threshold: 58575 },
        { rate: 0.24, threshold: 109225 },
        { rate: 0.32, threshold: 195950 },
        { rate: 0.35, threshold: 245100 },
        { rate: 0.37, threshold: 591975 }
      ],
      married: [
        { rate: 0.10, threshold: 27700 },
        { rate: 0.12, threshold: 49700 },
        { rate: 0.22, threshold: 117150 },
        { rate: 0.24, threshold: 218450 },
        { rate: 0.32, threshold: 391900 },
        { rate: 0.35, threshold: 490200 },
        { rate: 0.37, threshold: 731200 }
      ],
      head: [
        { rate: 0.10, threshold: 20800 },
        { rate: 0.12, threshold: 36500 },
        { rate: 0.22, threshold: 80650 },
        { rate: 0.24, threshold: 116150 },
        { rate: 0.32, threshold: 202900 },
        { rate: 0.35, threshold: 252050 },
        { rate: 0.37, threshold: 598900 }
      ]
    }[filingStatus];

    let tax = 0;
    let remainingIncome = annualTaxableIncome;

    for (let i = 0; i < brackets.length; i++) {
      const currentBracket = brackets[i];
      const nextBracket = brackets[i + 1];
      
      // Calculate the income taxable in this bracket
      const bracketStart = currentBracket.threshold;
      const bracketEnd = nextBracket ? nextBracket.threshold : Infinity;
      const taxableInBracket = Math.min(
        Math.max(0, remainingIncome - bracketStart),
        bracketEnd - bracketStart
      );

      // Calculate tax for this bracket
      tax += taxableInBracket * currentBracket.rate;

      if (remainingIncome <= bracketEnd) break;
    }

    return tax;
  };

  useEffect(() => {
    calculatePaycheck();
  }, [
    payRate, hoursPerWeek, payFrequency, state, filingStatus,
    allowances, age, retirementContributions, healthInsurance,
    otherDeductions, incomeType, salary, spouseIncome, showSpouseIncome
  ]);

  const chartData = {
    labels: ['Federal Tax', 'State Tax', 'Social Security', 'Medicare', '401(k)', 'Insurance', 'Take Home'],
    datasets: [{
      label: 'Pay Distribution',
      data: [
        payPeriodDetails.federal,
        payPeriodDetails.state,
        payPeriodDetails.socialSecurity,
        payPeriodDetails.medicare,
        payPeriodDetails.retirement401k,
        payPeriodDetails.insurance,
        payPeriodDetails.net
      ],
      backgroundColor: [
        'rgba(239, 68, 68, 0.8)',
        'rgba(245, 158, 11, 0.8)',
        'rgba(59, 130, 246, 0.8)',
        'rgba(99, 102, 241, 0.8)',
        'rgba(16, 185, 129, 0.8)',
        'rgba(139, 92, 246, 0.8)',
        'rgba(16, 185, 129, 0.8)',
      ],
      borderWidth: 0
    }]
  };

  return (
    <div className="min-h-screen bg-[var(--background)] p-6">
      <div className="max-w-7xl mx-auto">
        <Navigation />
        
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-3 rounded-xl bg-emerald-500/10">
              <Calculator className="w-10 h-10 text-emerald-500" />
            </div>
            <h1 className="text-4xl font-bold">Take Home Pay Calculator</h1>
          </div>
          <p className="text-slate-400 text-lg">
            Calculate your take-home pay after taxes and deductions
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div className="glass-card p-6">
              <h3 className="text-xl font-semibold mb-6">Income Details</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block text-sm text-slate-400">Income Type</label>
                  <select
                    value={incomeType}
                    onChange={(e) => setIncomeType(e.target.value as 'hourly' | 'salary')}
                    className="glass-input px-4 py-2 w-full rounded-lg"
                  >
                    <option value="hourly">Hourly</option>
                    <option value="salary">Salary</option>
                  </select>
                </div>

                {incomeType === 'hourly' ? (
                  <>
                    <div className="space-y-2">
                      <label className="block text-sm text-slate-400">Hourly Rate</label>
                      <div className="relative">
                        <DollarSign className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                        <input
                          type="number"
                          value={payRate}
                          onChange={(e) => setPayRate(Number(e.target.value))}
                          className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm text-slate-400">Hours Per Week</label>
                      <div className="relative">
                        <Clock className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                        <input
                          type="number"
                          value={hoursPerWeek}
                          onChange={(e) => setHoursPerWeek(Number(e.target.value))}
                          className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                          min="0"
                          max="168"
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="space-y-2">
                    <label className="block text-sm text-slate-400">Annual Salary</label>
                    <div className="relative">
                      <DollarSign className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                      <input
                        type="number"
                        value={salary}
                        onChange={(e) => setSalary(Number(e.target.value))}
                        className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                        min="0"
                        step="1000"
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <label className="block text-sm text-slate-400">Pay Frequency</label>
                  <select
                    value={payFrequency}
                    onChange={(e) => setPayFrequency(e.target.value as typeof payFrequency)}
                    className="glass-input px-4 py-2 w-full rounded-lg"
                  >
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Bi-Weekly</option>
                    <option value="semimonthly">Semi-Monthly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm text-slate-400">State</label>
                  <select
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    className="glass-input px-4 py-2 w-full rounded-lg"
                  >
                    {STATE_TAX_RATES.map(state => (
                      <option key={state.state} value={state.state}>
                        {state.state} ({state.rate}% - {state.notes})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm text-slate-400">Filing Status</label>
                  <select
                    value={filingStatus}
                    onChange={(e) => {
                      setFilingStatus(e.target.value as 'single' | 'married' | 'head');
                      if (e.target.value === 'married') {
                        setShowSpouseIncome(true);
                      } else {
                        setShowSpouseIncome(false);
                      }
                    }}
                    className="glass-input px-4 py-2 w-full rounded-lg"
                  >
                    <option value="single">Single</option>
                    <option value="married">Married Filing Jointly</option>
                    <option value="head">Head of Household</option>
                  </select>
                </div>
              </div>

              {showSpouseIncome && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-6 space-y-4"
                >
                  <h4 className="font-medium flex items-center gap-2">
                    <Users className="w-5 h-5 text-emerald-500" />
                    Spouse Income
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="block text-sm text-slate-400">Income Type</label>
                      <select
                        value={spouseIncome.incomeType}
                        onChange={(e) => setSpouseIncome({ 
                          ...spouseIncome, 
                          incomeType: e.target.value as 'hourly' | 'salary' 
                        })}
                        className="glass-input px-4 py-2 w-full rounded-lg"
                      >
                        <option value="hourly">Hourly</option>
                        <option value="salary">Salary</option>
                      </select>
                    </div>

                    {spouseIncome.incomeType === 'hourly' ? (
                      <>
                        <div className="space-y-2">
                          <label className="block text-sm text-slate-400">Hourly Rate</label>
                          <div className="relative">
                            <DollarSign className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                            <input
                              type="number"
                              value={spouseIncome.payRate}
                              onChange={(e) => setSpouseIncome({ 
                                ...spouseIncome, 
                                payRate: Number(e.target.value)
                              })}
                              className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label className="block text-sm text-slate-400">Hours Per Week</label>
                          <div className="relative">
                            <Clock className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                            <input
                              type="number"
                              value={spouseIncome.hoursPerWeek}
                              onChange={(e) => setSpouseIncome({ 
                                ...spouseIncome, 
                                hoursPerWeek: Number(e.target.value)
                              })}
                              className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                              min="0"
                              max="168"
                            />
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="space-y-2">
                        <label className="block text-sm text-slate-400">Annual Salary</label>
                        <div className="relative">
                          <DollarSign className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                          <input
                            type="number"
                            value={spouseIncome.salary}
                            onChange={(e) => setSpouseIncome({ 
                              ...spouseIncome, 
                              salary: Number(e.target.value)
                            })}
                            className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                            min="0"
                            step="1000"
                          />
                        </div>
                      </div>
                    )}

                    <div className="space-y-2">
                      <label className="block text-sm text-slate-400">Self-Employment Income</label>
                      <div className="relative">
                        <DollarSign className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                        <input
                          type="number"
                          value={spouseIncome.selfEmployment}
                          onChange={(e) => setSpouseIncome({ ...spouseIncome, selfEmployment: Number(e.target.value) })}
                          className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                          min="0"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm text-slate-400">Other Income</label>
                      <div className="relative">
                        <DollarSign className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                        <input
                          type="number"
                          value={spouseIncome.otherIncome}
                          onChange={(e) => setSpouseIncome({ ...spouseIncome, otherIncome: Number(e.target.value) })}
                          className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                          min="0"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm text-slate-400">Federal Withholding</label>
                      <div className="relative">
                        <DollarSign className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                        <input
                          type="number"
                          value={spouseIncome.federalWithholding}
                          onChange={(e) => setSpouseIncome({ ...spouseIncome, federalWithholding: Number(e.target.value) })}
                          className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                          min="0"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm text-slate-400">Estimated Tax Payments</label>
                      <div className="relative">
                        <DollarSign className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                        <input
                          type="number"
                          value={spouseIncome.estimatedTaxPayments}
                          onChange={(e) => setSpouseIncome({ ...spouseIncome, estimatedTaxPayments: Number(e.target.value) })}
                          className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                          min="0"
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            <div className="glass-card p-6">
              <h3 className="text-xl font-semibold mb-6">Retirement Contributions</h3>
              <div className="space-y-4">
                <button
                  onClick={addRetirementContribution}
                  className="px-4 py-2 bg-emerald-500/10 text-emerald-500 rounded-lg hover:bg-emerald-500/20 transition-colors"
                >
                  Add Retirement Account
                </button>

                <div className="space-y-2">
                  <label className="block text-sm text-slate-400">Age</label>
                  <input
                    type="number"
                    value={age}
                    onChange={(e) => setAge(Number(e.target.value))}
                    className="glass-input px-4 py-2 w-full rounded-lg"
                    min="0"
                    max="100"
                  />
                </div>

                {retirementContributions.map((contribution, index) => {
                  const account = RETIREMENT_ACCOUNTS[contribution.type];
                  const annualLimit = age >= (account.catchupAge || 0)
                    ? account.limit + (account.catchup || 0)
                    : account.limit;
                  
                  return (
                    <div key={index} className="glass-card p-4 border border-emerald-500/20">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="block text-sm text-slate-400">Account Type</label>
                          <select
                            value={contribution.type}
                            onChange={(e) => updateRetirementContribution(index, { 
                              type: e.target.value as keyof typeof RETIREMENT_ACCOUNTS 
                            })}
                            className="glass-input px-4 py-2 w-full rounded-lg"
                          >
                            {Object.entries(RETIREMENT_ACCOUNTS).map(([key, account]) => (
                              <option key={key} value={key}>
                                {account.label} (Limit: ${annualLimit.toLocaleString()})
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="space-y-2">
                          <label className="block text-sm text-slate-400">Contribution (%)</label>
                          <div className="relative">
                            <input
                              type="number"
                              value={contribution.amount}
                              onChange={(e) => updateRetirementContribution(index, { 
                                amount: Number(e.target.value)
                              })}
                              className="glass-input px-4 py-2 w-full rounded-lg"
                              min="0"
                              max="100"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 text-sm text-slate-400">
                        <p>{RETIREMENT_ACCOUNTS[contribution.type].description}</p>
                        {RETIREMENT_ACCOUNTS[contribution.type].incomeLimit && (
                          <p className="mt-2">
                            Income limits: ${RETIREMENT_ACCOUNTS[contribution.type].incomeLimit.single.toLocaleString()} (Single) / 
                            ${RETIREMENT_ACCOUNTS[contribution.type].incomeLimit.joint.toLocaleString()} (Joint)
                          </p>
                        )}
                      </div>

                      <button
                        onClick={() => removeRetirementContribution(index)}
                        className="mt-4 px-4 py-2 bg-red-500/10 text-red-500 rounded-lg hover:bg-red-500/20 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="glass-card p-6">
              <h3 className="text-xl font-semibold mb-6">Deductions</h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="block text-sm text-slate-400">Health Insurance (per period)</label>
                  <div className="relative">
                    <Heart className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <input
                      type="number"
                      value={healthInsurance}
                      onChange={(e) => setHealthInsurance(Number(e.target.value))}
                      className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                      min="0"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm text-slate-400">Other Deductions</label>
                  <div className="relative">
                    <DollarSign className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <input
                      type="number"
                      value={otherDeductions}
                      onChange={(e) => setOtherDeductions(Number(e.target.value))}
                      className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                      min="0"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="glass-card p-6">
              <h3 className="text-xl font-semibold mb-6">Pay Summary</h3>
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-400 mb-1">Gross Pay</p>
                    <p className="text-2xl font-bold text-emerald-500">
                      ${payPeriodDetails.gross.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400 mb-1">Net Pay</p>
                    <p className="text-2xl font-bold text-emerald-500">
                      ${payPeriodDetails.net.toFixed(2)}
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-medium text-slate-300">Deductions Breakdown</h4>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Federal Tax</span>
                      <span className="text-red-500">${payPeriodDetails.federal.toFixed(2)}</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">State Tax</span>
                      <span className="text-amber-500">${payPeriodDetails.state.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Social Security</span>
                      <span className="text-blue-500">${payPeriodDetails.socialSecurity.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Medicare</span>
                      <span className="text-indigo-500">${payPeriodDetails.medicare.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">401(k)</span>
                      <span className="text-emerald-500">${payPeriodDetails.retirement401k.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Insurance</span>
                      <span className="text-violet-500">${payPeriodDetails.insurance.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <h4 className="font-medium text-slate-300 mb-4">Distribution Chart</h4>
                  <FinancialChart
                    type="bar"
                    data={chartData}
                    height={300}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <Newsletter 
            title="Stay Updated on Tax Changes"
            subtitle="Get the latest tax updates and financial planning tips delivered to your inbox."
          />
        </div>
      </div>
    </div>
  );
};

export default TakeHomeCalculator;